import { useState, useCallback, useEffect } from "react";
import { atom, useRecoilState } from "recoil";
import { IUser } from "src/domains";
import {
  queryGetUsers,
  mutationUserBlock,
  mutationUserUnblock,
} from "src/graphql";
import {
  blockUserEventEmit,
  unblockUserEventEmit,
  TabChangeEvent,
  LogoutEvent,
} from "src/utils/event";
import { AppState, AppStateStatus } from "react-native";
import { EventRegister } from "react-native-event-listeners";

export const blockState = atom<IUser[]>({
  key: "blockState",
  default: [],
});

export const useBlock = () => {
  const [users, setUsers] = useRecoilState<IUser[]>(blockState);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetUsers({
        filter: {
          isBlocked: true,
        },
      });
      if (_isMount) {
        setUsers(response.data.getUsers?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setUsers]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "MyPage") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setUsers([]);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [users, loading, error, refresh] as const;
};

export const useBlockActions = () => {
  const [, setUsers] = useRecoilState<IUser[]>(blockState);

  const refresh = useCallback(async () => {
    const response = await queryGetUsers({
      filter: {
        isBlocked: true,
      },
    });
    setUsers(response.data.getUsers?.items || []);
  }, [setUsers]);

  const block = useCallback(
    async (id: string) => {
      // console.log('block...', id)
      await mutationUserBlock(id);
      blockUserEventEmit(id);
      refresh();
    },
    [refresh]
  );

  const unblock = useCallback(
    async (id: string) => {
      await mutationUserUnblock(id);
      unblockUserEventEmit(id);
      refresh();
    },
    [refresh]
  );

  return { block, unblock } as const;
};
